import {FC} from 'react'

export type AiAssignmentSummary = {
  state: string
  outcome?: string | null
  recommendedInterpreterId?: string | null
  windowClosesAtUtc?: string | null
  eligibleCandidateCount: number
  qualifiedCandidateCount: number
  smsSentCount: number
  bannerText?: string
}

type Props = {
  summary: AiAssignmentSummary | null
  onOpenActivity?: () => void
}

const IN_SCOPE_COMMUNICATION_TYPES = ['os', 'oc']

export const isAiAssignmentInScope = (communicationType?: string | null) =>
  IN_SCOPE_COMMUNICATION_TYPES.includes((communicationType ?? '').trim().toLowerCase())

type BannerTone = {
  bg: string
  icon: string
  color: string
  border?: string
  text?: string
  iconBg?: string
}

const TEXT_DEFAULT = '#181c32'

const TONE: Record<string, BannerTone> = {
  Recommended: {
    bg: '#fffbeb',
    icon: 'bi-robot',
    color: '#9e48ed',
    text: '#92400e',
    iconBg: '#ffffff',
  },
  Assigned: {bg: '#e8fff3', border: '#50cd89', icon: 'bi-check-circle', color: '#0f7c4a'},
  AwaitingConfirmation: {
    bg: '#f8f5ff',
    border: '#7239ea',
    icon: 'bi-hourglass-split',
    color: '#7239ea',
  },
  Outreach: {bg: '#f1faff', border: '#009ef7', icon: 'bi-broadcast', color: '#0069a8'},
  Selecting: {bg: '#f1faff', border: '#009ef7', icon: 'bi-hourglass-split', color: '#0069a8'},
  Qualifying: {bg: '#f1faff', border: '#009ef7', icon: 'bi-hourglass-split', color: '#0069a8'},
  AwaitingPool: {bg: '#f9f9f9', border: '#b5b5c3', icon: 'bi-hourglass', color: '#5e6278'},
  Created: {bg: '#f9f9f9', border: '#b5b5c3', icon: 'bi-hourglass', color: '#5e6278'},
  NoAcceptances: {
    bg: '#fff8dd',
    border: '#ffc700',
    icon: 'bi-exclamation-triangle',
    color: '#9a7500',
  },
  NoQualifiedCandidates: {
    bg: '#fff8dd',
    border: '#ffc700',
    icon: 'bi-exclamation-triangle',
    color: '#9a7500',
  },
  Abandoned: {bg: '#f9f9f9', border: '#b5b5c3', icon: 'bi-slash-circle', color: '#5e6278'},
}

const DEFAULT_TONE: BannerTone = {
  bg: '#f9f9f9',
  border: '#b5b5c3',
  icon: 'bi-robot',
  color: '#5e6278',
}

const BANNER_LABEL = 'AI Assignment Agent:'

const formatLocal = (iso?: string | null) => {
  if (!iso) return null
  const parsed = new Date(iso)
  if (Number.isNaN(parsed.getTime())) return null
  return parsed.toLocaleString(undefined, {
    month: 'short',
    day: 'numeric',
    hour: 'numeric',
    minute: '2-digit',
  })
}

const AiAgentBanner: FC<Props> = ({summary, onOpenActivity}) => {
  if (!summary) return null

  const tone = TONE[summary.state] ?? DEFAULT_TONE
  const windowCloses = formatLocal(summary.windowClosesAtUtc)
  const isRunning = summary.state === 'Outreach' || summary.state === 'Selecting'

  const bannerText = summary.bannerText ?? ''
  const hasLabel = bannerText.startsWith(BANNER_LABEL)
  const bannerBody = hasLabel ? bannerText.slice(BANNER_LABEL.length).trim() : bannerText

  return (
    <div
      className='d-flex align-items-start'
      style={{
        background: tone.bg,
        borderLeft: tone.border ? `4px solid ${tone.border}` : undefined,
        borderRadius: '0.475rem',
        padding: '12px 16px',
        marginBottom: 16,
        gap: 12,
      }}
      data-testid='ai-agent-banner'
    >
      {tone.iconBg ? (
        <span
          style={{
            background: tone.iconBg,
            borderRadius: 8,
            width: 28,
            height: 28,
            flexShrink: 0,
            display: 'inline-flex',
            alignItems: 'center',
            justifyContent: 'center',
            boxShadow: '0 1px 3px rgba(146, 64, 14, 0.12)',
          }}
        >
          <i
            className={`bi ${tone.icon}`}
            style={{color: tone.color, fontSize: '1rem', lineHeight: 1}}
            aria-hidden='true'
          />
        </span>
      ) : (
        <i
          className={`bi ${tone.icon}`}
          style={{color: tone.color, fontSize: '1.1rem', lineHeight: 1.4}}
          aria-hidden='true'
        />
      )}

      <div style={{flex: 1, minWidth: 0}}>
        <div
          style={{
            fontWeight: hasLabel ? 400 : 600,
            color: tone.text ?? TEXT_DEFAULT,
            fontSize: '0.95rem',
          }}
        >
          {hasLabel && <strong style={{fontWeight: 700}}>{BANNER_LABEL} </strong>}
          {bannerBody}
        </div>

        <div
          className='d-flex flex-wrap align-items-center'
          style={{gap: 14, marginTop: 4, color: '#5e6278', fontSize: '0.85rem'}}
        >
          {summary.eligibleCandidateCount > 0 && (
            <span>
              {summary.qualifiedCandidateCount} qualified of {summary.eligibleCandidateCount}{' '}
              eligible
            </span>
          )}

          {summary.smsSentCount > 0 && <span>{summary.smsSentCount} texted</span>}

          {isRunning && windowCloses && <span>Outreach closes {windowCloses}</span>}
        </div>
      </div>

      {onOpenActivity && (
        <button
          type='button'
          className='btn btn-sm btn-light-primary'
          onClick={onOpenActivity}
          style={{whiteSpace: 'nowrap'}}
        >
          <i className='bi bi-chat-left-text' style={{marginRight: 6}} />
          SMS Activities
        </button>
      )}
    </div>
  )
}

export default AiAgentBanner
